import { unwrapMessage, wrapMessage } from '@support/widget-contracts';
import type { FrameToHostMessage, HostToFrameMessage } from '@support/widget-contracts';

/**
 * The origin of the page that embedded us. Learned from the first inbound
 * message rather than configured, because `ready` has to go out before anyone
 * has told the frame who its parent is — and the frame has no other way to know
 * (reading `document.referrer` is unreliable and `parent.location` is blocked
 * cross-origin).
 *
 * `'*'` is therefore only ever used for `ready`, which carries no payload.
 * Everything after it is addressed to the origin that actually spoke to us.
 */
let hostOrigin = '*';

export const postToHost = (message: FrameToHostMessage): void => {
  window.parent.postMessage(wrapMessage(message), hostOrigin);
};

/**
 * Subscribes to host messages; returns the unsubscribe function.
 *
 * Two filters, both load-bearing. `event.source` keeps a sibling frame or an
 * embedded ad from talking to us, and the channel marker inside the envelope
 * keeps the analytics/chat/extension traffic that any host page carries out of
 * our switch statement.
 */
export const listenToHost = (handler: (message: HostToFrameMessage) => void): (() => void) => {
  const onMessage = (event: MessageEvent): void => {
    if (event.source !== window.parent) {
      return;
    }

    const message = unwrapMessage<HostToFrameMessage>(event.data);

    if (!message) {
      return;
    }

    // INVARIANT, and the reason this line is safe: every frame->host message is
    // data-free — `ready`, `loaded`, `resize` (two compile-time constants),
    // `openState` (a boolean), `pong`, `identityExpired`. Adopting the origin of
    // whoever last spoke to us therefore cannot leak anything, because there is
    // nothing in the outbound union to leak.
    //
    // The moment ANY frame->host message carries a real value — a contact's
    // name, a ticket id, an unread count — this line becomes the bug: it would
    // send that value to whichever origin posted last. Add such a message and
    // this must become a pinned origin, captured once from `init`.
    //
    // `identityExpired` is the one to watch: it is payload-free today, but it is
    // the message whose natural next version is not ("expired for contact X",
    // "retry after N"). If anyone extends it, that is the edit that trips this
    // invariant — and it will not look like a change to the bridge at all.
    //
    // Not pinned today because it would defend against nothing: the
    // `event.source !== window.parent` guard above already restricts this to the
    // embedding page, and a document's origin cannot change without a navigation
    // that reloads this frame anyway. Pinning would only add a way to go
    // silently deaf if that assumption were ever wrong.
    hostOrigin = event.origin;

    handler(message);
  };

  window.addEventListener('message', onMessage);

  return () => window.removeEventListener('message', onMessage);
};
